import React, { useState } from "react";
import "./basicInformation.css";
import { useNavigate } from "react-router-dom";
import {
  resetOnboardingInfo,
  updateOnboardingInfo,
} from "../../redux/attorneyOnboardingSlice";
import { useDispatch, useSelector } from "react-redux";
import BackIcon from "./assets/backIcon.png";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import NarrowInputField from "../../components/input/new/narrowInputField";
import RadioButton from "../../components/input/new/radioButton";
import NarrowDropdownHierarchy from "../../components/input/new/narrowDropdownHierarchy";
import IconCheckbox from "../../components/input/new/iconCheckbox";
import StepFooter from "./stepFooter";
import TextInputWithPercentage from "../../components/input/new/textInputWithPercentage";
import { getPracticeAreaLabel } from "../../helpers/stringHelper";
import { practiceAreas, suffixes } from "./schoolData";
import NarrowDatePicker from "../../components/input/new/narrowDatePicker";
import { parseISO, format } from "date-fns";

const BasicInformation = () => {
  // Required fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [specialty, setSpecialty] = useState([]);
  const [specialtyDescriptions, setSpecialtyDescriptions] = useState({});
  const [suffix, setSuffix] = useState([]);
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [noSpecialty, setNoSpecialty] = useState(false);
  const [dob, setDOB] = useState("");

  const userId = useSelector(
    (state) => state.attorneyData.attorneyData?.user_id
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const goBackToProfessionals = () => {
    dispatch(resetOnboardingInfo());
    navigate(`/attorney/${userId}/home`);
  };

  const handleSpecialtyChange = (vals) => {
    setSpecialty(vals);
    const newDefault = (100 / vals.length).toFixed(0); // e.g., 3 specialties → "33", 4 → "25"
    const newDescriptions = {};
    vals.forEach((val) => {
      newDescriptions[val] = {
        percentage: newDefault,
        description: specialtyDescriptions[val]?.description || "",
      };
    });
    setSpecialtyDescriptions(newDescriptions);
  };

  // Handle description change for a specific specialty
  const handleDescriptionChange = (specialty, value) => {
    setSpecialtyDescriptions((prev) => ({
      ...prev,
      [specialty]: { ...prev[specialty], description: value },
    }));
  };

  // Handle percentage change for a specific specialty
  const handlePercentageChange = (specialty, value) => {
    setSpecialtyDescriptions((prev) => ({
      ...prev,
      [specialty]: { ...prev[specialty], percentage: value },
    }));
  };

  const handleNext = () => {
    dispatch(
      updateOnboardingInfo({
        firstName,
        lastName,
        gender,
        practiceAreas: specialty,
        specialty: specialtyDescriptions, // Added this field
        dob: dob,
        professionalSuffixes: suffix,
        adminFirstName,
        adminLastName,
        adminPhone,
        adminEmail,
      })
    );
  };

  // Determine if the StepFooter's Continue button should be disabled.
  // All of these fields are required:
  // firstName, lastName, gender, suffix (at least one), specialty (at least one),
  // adminFirstName, adminLastName, adminPhone, and adminEmail.
  const isFooterDisabled =
    !firstName.trim() ||
    !lastName.trim() ||
    !gender ||
    suffix.length === 0 ||
    specialty.length === 0 ||
    !adminFirstName.trim() ||
    !adminLastName.trim() ||
    !adminPhone.trim() ||
    !adminEmail.trim();

  return (
    <div className="basic-info-container">
      <div className="category-profile-types">
        <div style={{ display: "flex", flexDirection: "column", width: "33%" }}>
          <div className="category-breadcrumb" onClick={goBackToProfessionals}>
            <img
              src={BackIcon}
              alt="Back Icon"
              style={{ width: "24px", height: "24px" }}
            />
            <a className="category-back-link">Back to professionals</a>
          </div>
          <h1 className="bar-number-title">Basic Information</h1>

          <div className="bar-number-row">
            <NarrowInputField
              name="First Name"
              title="First Name"
              placeholder="Enter First Name"
              isRequired={true}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              halfWidth={false}
            />

            <NarrowInputField
              name="Last Name"
              title="Last Name"
              placeholder="Enter Last Name"
              isRequired={true}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              halfWidth={false}
            />
          </div>

          <NarrowDatePicker
            title="Date of birth"
            subLabel="Please select your date of birth"
            placeholder="YYYY-MM-DD"
            isRequired={true}
            value={dob ? parseISO(dob) : null}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                setDOB(format(date, "yyyy-MM-dd"));
              } else {
                setDOB("");
              }
            }}
            halfWidth={false}
            name="startDate"
          />
          <div style={{ height: "1.5rem" }} />

          <div className="radio-row">
            <p className="row-title"> Gender </p>
            <RadioButton
              label="Female"
              name="gender"
              value="female"
              checked={gender === "female"}
              onChange={(e) => setGender(e.target.value)}
            />
            <RadioButton
              label="Male"
              name="gender"
              value="male"
              checked={gender === "male"}
              onChange={(e) => setGender(e.target.value)}
            />
          </div>

          <div style={{ height: "1.5rem" }} />

          <NarrowDropdownHierarchy
            title="Professional Suffixes"
            placeholder="Choose an option"
            subLabel="Select all that apply"
            items={suffixes}
            isRequired={true}
            value={suffix}
            onChange={(vals) => setSuffix(vals)}
            multiple={true}
            hierarchical={false}
          />

          <hr className="h-rule" />

          <NarrowDropdownHierarchy
            title="Specialty"
            placeholder="Choose an option"
            subLabel="Select all that apply"
            items={practiceAreas}
            isRequired={true}
            value={specialty}
            onChange={handleSpecialtyChange}
            multiple={true}
            hierarchical={true}
          />

          <IconCheckbox
            label="I can't find my specialty"
            checked={noSpecialty}
            onChange={setNoSpecialty}
          />

          <div style={{ height: "1.5rem" }} />
          {specialty.length > 0 && (
            <div>
              {specialty.map((spec) => {
                const defaultPercentage = (100 / specialty.length).toFixed(2);
                const percentage =
                  specialtyDescriptions[spec]?.percentage || defaultPercentage;
                return (
                  <div key={spec}>
                    <TextInputWithPercentage
                      label={getPracticeAreaLabel(spec)}
                      percentage={percentage}
                      description={
                        specialtyDescriptions[spec]?.description || ""
                      }
                      onPercentageChange={(value) =>
                        handlePercentageChange(spec, value)
                      }
                      onDescriptionChange={(value) =>
                        handleDescriptionChange(spec, value)
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ height: "1.5rem" }} />

          <hr className="h-rule" />

          <p className="basic-info-subtitle2">Administrative contact</p>
          <p className="basic-info-subtitle3">
            If needed, we’ll reach out to this person to verify your information
          </p>
          <div style={{ height: "1rem" }} />

          <div className="bar-number-row">
            <NarrowInputField
              name="Contact first name"
              title="Contact first name"
              placeholder="Enter first name"
              isRequired={true}
              value={adminFirstName}
              onChange={(e) => setAdminFirstName(e.target.value)}
              halfWidth={false}
            />

            <NarrowInputField
              name="Contact last name"
              title="Contact last name"
              placeholder="Enter last name"
              isRequired={true}
              value={adminLastName}
              onChange={(e) => setAdminLastName(e.target.value)}
              halfWidth={false}
            />
          </div>

          <NarrowInputField
            name="Contact phone number"
            title="Phone number"
            placeholder="Enter phone number"
            isRequired={true}
            value={adminPhone}
            onChange={(e) => setAdminPhone(e.target.value)}
            halfWidth={false}
          />

          <NarrowInputField
            name="Contact email address"
            title="Email address"
            placeholder="Enter email address"
            isRequired={true}
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            halfWidth={false}
          />
        </div>
      </div>
      <StepFooter
        currentStep={3}
        totalSteps={9}
        location="/attorney/setup/locations"
        onNext={handleNext}
        disabled={isFooterDisabled} // Disable the Continue button if any required field is missing
      />
    </div>
  );
};

export default BasicInformation;
